import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  TextField
} from '@mui/material';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';

import { useTranslate } from 'src/locales';

import FormProvider from 'src/components/hook-form/form-provider';
import RHFCountryCodeSelector from 'src/components/hook-form/rhf-country-code-selector';
import RHFTextField from 'src/components/hook-form/rhf-text-field';
import Iconify from 'src/components/iconify';
import ListSkeleton from 'src/components/list/list-skeleton';
import { useGetApplicationCandidateProfileQuery, useUpdateApplicationCandidateProfileMutation } from 'src/services/applications/applications.service';
import { generateUpdateProfileError } from 'src/services/applications/applications.utils';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';

type Props = {
  applicationId: string;
};

export function ApplicationInfoCard({ applicationId }: Props) {

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  const dispatch = useAppDispatch();

  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateApplicationCandidateProfileMutation();

  const [isEditing, setIsEditing] = useState(false);

  const { currentData: applicationProfile, isFetching: isFetchingApplication } = useGetApplicationCandidateProfileQuery(
    applicationId as string,
    {
      skip: !applicationId,
    }
  );

  const ProfileSchema = Yup.object().shape({
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    country_code: Yup.string(),
    phone_number: Yup.string(),
    position_title: Yup.string(),
  });

  const defaultValues = {
    first_name: '',
    last_name: '',
    country_code: '',
    phone_number: '',
    position_title: '',
  };

  const methods = useForm({
    resolver: yupResolver(ProfileSchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = methods;

  useEffect(() => {

    if (applicationProfile) {
      reset({
        first_name: applicationProfile.first_name || '',
        last_name: applicationProfile.last_name || '',
        country_code: applicationProfile.country_code || '',
        phone_number: applicationProfile.phone_number || '',
        position_title: applicationProfile.position_title || '',
      });
    }

  }, [applicationProfile, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateProfile({
        applicationId,
        data: {
          first_name: data.first_name,
          last_name: data.last_name,
          country_code: data.country_code,
          phone_number: data.phone_number,
          position_title: data.position_title,
        },
      }).unwrap();

      dispatch(rootApi.util.invalidateTags([{ type: 'CandidateProfile', id: applicationProfile?.id }]));
      dispatch(rootApi.util.invalidateTags([{ type: 'Applications', id: applicationId }]));

      enqueueSnackbar(t('applications.api.update_profile.success'), { variant: 'success' });
      setIsEditing(false);
    } catch (error) {
      enqueueSnackbar(generateUpdateProfileError(error), { variant: 'error' });
      setIsEditing(false);
    }
  });

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
              <Iconify icon="solar:user-id-broken" />
              <Box>{t('candidates.detail.details.title')}</Box>
            </Stack>
            <IconButton
              disabled={isFetchingApplication || isEditing}
              onClick={() => setIsEditing(!isEditing)}
            >
              <Iconify icon="eva:edit-fill" />
            </IconButton>
          </Stack>
        }
      />
      <CardContent sx={{ mt: -2 }}>
        {isFetchingApplication && <ListSkeleton height={20} />}

        {applicationProfile && (
          <>
            {isEditing ? (
              <FormProvider methods={methods} onSubmit={onSubmit}>
                <Grid container spacing={2}>
                  <Grid xs={6} sm={6}>
                    <RHFTextField
                      name="first_name"
                      label={t('common.form_labels.first_name')}
                    />
                  </Grid>

                  <Grid xs={6} sm={6}>
                    <RHFTextField
                      name="last_name"
                      label={t('common.form_labels.last_name')}
                    />
                  </Grid>

                  <Grid xs={12} sm={12}>
                    <TextField
                      fullWidth
                      disabled
                      label={t('common.form_labels.email')}
                      value={applicationProfile.email || '--'}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <Iconify icon="mdi:email-outline" sx={{ mr: 1 }} />
                        )
                      }}
                    />
                  </Grid>

                  <Grid xs={3} sm={3}>
                    <RHFCountryCodeSelector
                      name="country_code"
                      label={t('account.profile.form.country_code.label')}
                      placeholder={t('account.profile.form.country_code.placeholder')}
                    />
                  </Grid>

                  <Grid xs={9} sm={9}>
                    <RHFTextField
                      name="phone_number"
                      label={t('account.profile.form.phone.label')}
                      InputProps={{
                        startAdornment: (
                          <Iconify icon="solar:phone-linear" sx={{ mr: 1 }} />
                        )
                      }}
                    />
                  </Grid>

                  <Grid xs={12} sm={12}>
                    <RHFTextField
                      fullWidth
                      name="position_title"
                      label={t('common.form_labels.position_title')}
                    />
                  </Grid>
                </Grid>

                <CardActions sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => setIsEditing(false)}
                    sx={{ mr: 1, width: '100%' }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                    disabled={!isDirty}
                    sx={{ width: '100%' }}
                  >
                    {t('common.save')}
                  </LoadingButton>
                </CardActions>
              </FormProvider>
            ) : (
              <>
                <Grid container spacing={2}>
                  <Grid xs={6} sm={6}>
                    <TextField
                      fullWidth
                      disabled
                      label={t('common.form_labels.first_name')}
                      value={applicationProfile?.first_name || ''}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>

                  <Grid xs={6} sm={6}>
                    <TextField
                      fullWidth
                      disabled
                      label={t('common.form_labels.last_name')}
                      value={applicationProfile?.last_name || ''}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>

                  <Grid xs={12} sm={12}>
                    <TextField
                      fullWidth
                      disabled
                      label={t('common.form_labels.email')}
                      value={applicationProfile.email || '--'}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <Iconify icon="mdi:email-outline" sx={{ mr: 1 }} />
                        )
                      }}
                    />
                  </Grid>

                  <Grid xs={12} sm={12}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        disabled
                        label={t('account.profile.form.phone.label')}
                        value={`${applicationProfile.country_code || ''} ${applicationProfile.phone_number || '--'}`}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <Iconify icon="solar:phone-linear" sx={{ mr: 1 }} />
                          )
                        }}
                      />
                    </Stack>
                  </Grid>

                  <Grid xs={12} sm={12}>
                    <TextField
                      fullWidth
                      disabled
                      label={t('common.form_labels.position_title')}
                      value={applicationProfile?.position_title || t('candidates.table.no_position_title')}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}